/**
 * Pure helpers for extracting a stable summary from the parser API
 * response. Used internally by `<SuccessSummary>` and exposed publicly
 * so embedders building their own post-import UI don't have to
 * reverse-engineer the response shape themselves.
 *
 * Verified against the real /v4/smart/parse response shape:
 *
 *   {
 *     meta:           { cas_type, generated_at, statement_period: { from, to } },
 *     investor:       { name, pan, email, mobile, address, pincode },
 *     mutual_funds:   [ { folio_number, amc, schemes: [...], value } ],
 *     demat_accounts: [ { bo_id, dp_name, holdings: { equities, demat_mutual_funds,
 *                         aifs, corporate_bonds, government_securities }, value } ],
 *     insurance:      { life_insurance_policies: [...] },
 *     nps:            [ { pran, funds: [...] } ],
 *     summary: {
 *       total_value: number,
 *       accounts: {
 *         mutual_funds: { count, total_value },  // count is *scheme* count
 *         demat:        { count, total_value },  // count is *account* count
 *         insurance:    { count, total_value },
 *         nps:          { count, total_value },
 *       }
 *     }
 *   }
 *
 * Older / wrapped responses (some CDSL/NSDL paths) may nest under
 * `data.*`, and the legacy v1 shape used `investor_info`, `folios`,
 * `holdings` at the top level. We probe both for safety.
 */

export interface ParsedPortfolioSummary {
  /** `summary.total_value` from the API. `null` when not present. */
  totalValue: number | null;
  /** Mutual fund folio count (`mutual_funds.length`). */
  folios: number | null;
  /**
   * Total demat holdings — sum of equities + ETFs/MF units +
   * bonds + AIFs + government securities across every demat account.
   */
  holdings: number | null;
  /** ISO-ish date or `meta.statement_period.to` from the API. */
  asOn: string | null;
  /** Friendly cas-type label (e.g. "Stocks (NSDL)", "Mutual Funds"). */
  casType: string | null;
  /** Investor's full name. */
  investorName: string | null;
}

const CAS_TYPE_LABELS: Record<string, string> = {
  CAMS_KFINTECH: 'Mutual Funds',
  CAMS: 'Mutual Funds',
  KFINTECH: 'Mutual Funds',
  CDSL: 'Stocks (CDSL)',
  NSDL: 'Stocks (NSDL)',
};

export function friendlyCasType(value: unknown): string | null {
  if (!value || typeof value !== 'string') return null;
  return CAS_TYPE_LABELS[value.toUpperCase()] || value;
}

export function extractPortfolioSummary(data: unknown): ParsedPortfolioSummary {
  const raw = data as any;
  const root = raw?.data ?? raw; // unwrap legacy `{ data: {...} }` envelopes
  const summary = root?.summary || {};
  const meta = root?.meta || {};

  // ── Total portfolio value ──────────────────────────────────────
  const totalValue =
    coerceNumber(summary.total_value) ??
    coerceNumber(summary.total_portfolio_value) ??
    coerceNumber(root?.total_value) ??
    null;

  // ── Folio count ────────────────────────────────────────────────
  const folioArray = Array.isArray(root?.mutual_funds)
    ? root.mutual_funds
    : Array.isArray(root?.folios)
    ? root.folios
    : null;
  const folios = folioArray
    ? folioArray.length
    : coerceNumber(summary.folios);

  // ── Holdings count ─────────────────────────────────────────────
  let holdings: number | null = null;
  if (Array.isArray(root?.holdings)) {
    holdings = root.holdings.length;
  } else if (Array.isArray(root?.demat_accounts)) {
    holdings = root.demat_accounts.reduce(
      (acc: number, account: any) => acc + countHoldingsInAccount(account),
      0,
    );
  }

  // ── As-on date ─────────────────────────────────────────────────
  const asOn =
    meta?.statement_period?.to ||
    meta?.generated_at ||
    summary.as_on_date ||
    summary.as_on ||
    null;

  // ── CAS type ───────────────────────────────────────────────────
  const casType =
    friendlyCasType(meta?.cas_type) ||
    friendlyCasType(root?.cas_type);

  // ── Investor name ──────────────────────────────────────────────
  const investorName =
    root?.investor?.name ||
    root?.investor_info?.name ||
    null;

  return {
    totalValue,
    folios,
    holdings,
    asOn,
    casType,
    investorName,
  };
}

/**
 * Count distinct holdings inside one demat account. Treats every
 * instrument bucket the same (equities, ETFs/demat MFs, AIFs,
 * corporate bonds, government securities). If a future shape adds a
 * new bucket we'll undercount silently — acceptable for a display
 * counter, not financial reconciliation.
 */
export function countHoldingsInAccount(account: any): number {
  const h = account?.holdings;
  if (!h || typeof h !== 'object') return 0;
  return (
    arrLen(h.equities) +
    arrLen(h.demat_mutual_funds) +
    arrLen(h.mutual_funds) +
    arrLen(h.aifs) +
    arrLen(h.corporate_bonds) +
    arrLen(h.government_securities)
  );
}

function arrLen(v: unknown): number {
  return Array.isArray(v) ? v.length : 0;
}

function coerceNumber(v: unknown): number | null {
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  if (typeof v === 'string') {
    const n = parseFloat(v.replace(/,/g, ''));
    return Number.isNaN(n) ? null : n;
  }
  return null;
}
